import React, { useState, useEffect, useCallback, useRef } from 'react';
import { liveApi } from '../../services/api';
import { formatINR } from '../../utils/formatters';
import { Broadcast, ArrowsClockwise, TrendUp, TrendDown } from '@phosphor-icons/react';

const REFRESH_SEC = 60;

function wsUrl() {
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  return backend.replace(/^http/, 'ws') + '/api/ws/live-indices';
}

export default function LiveNseBseTab() {
  const [indices, setIndices] = useState([]);
  const [status, setStatus] = useState([]);
  const [gainers, setGainers] = useState([]);
  const [losers, setLosers] = useState([]);
  const [fiiDii, setFiiDii] = useState([]);
  const [corpActions, setCorpActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ind, stat, g, l, ff, ca] = await Promise.all([
        liveApi.nseIndices().catch(() => ({ data: { indices: [] } })),
        liveApi.nseMarketStatus().catch(() => ({ data: { markets: [] } })),
        liveApi.nseGainers().catch(() => ({ data: { movers: [] } })),
        liveApi.nseLosers().catch(() => ({ data: { movers: [] } })),
        liveApi.nseFiiDii().catch(() => ({ data: { flows: [] } })),
        liveApi.bseCorpActions().catch(() => ({ data: { actions: [] } })),
      ]);
      setIndices(ind.data.indices || []);
      setStatus(stat.data.markets || []);
      setGainers(g.data.movers || []);
      setLosers(l.data.movers || []);
      setFiiDii(ff.data.flows || []);
      setCorpActions(ca.data.actions || []);
      setLastUpdated(new Date());
    } catch (e) {
      console.error('live nse/bse load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, REFRESH_SEC * 1000);
    return () => clearInterval(t);
  }, [loadAll]);

  // WebSocket for the 8 key indices — tick every 15s
  useEffect(() => {
    let ws;
    let reconnectTimer;
    const connect = () => {
      try {
        ws = new WebSocket(wsUrl());
        wsRef.current = ws;
        ws.onopen = () => setWsConnected(true);
        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (msg.indices) {
              // Merge WS updates onto existing indices state
              setIndices((prev) => {
                const map = new Map(prev.map((i) => [i.symbol, i]));
                for (const upd of msg.indices) {
                  const existing = map.get(upd.symbol);
                  if (existing) {
                    map.set(upd.symbol, { ...existing, ...upd });
                  }
                }
                return Array.from(map.values());
              });
              setLastUpdated(new Date());
            }
          } catch (err) {
            console.error('ws parse failed', err);
          }
        };
        ws.onclose = () => {
          setWsConnected(false);
          reconnectTimer = setTimeout(connect, 5000);
        };
        ws.onerror = () => { setWsConnected(false); };
      } catch (e) {
        console.error('ws connect failed', e);
      }
    };
    connect();
    return () => {
      if (ws && ws.readyState !== WebSocket.CONNECTING && ws.readyState !== WebSocket.CLOSED) {
        ws.close();
      }
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, []);

  return (
    <div className="space-y-6" data-testid="live-nsebse-tab-content">
      <TopBar onRefresh={loadAll} loading={loading} lastUpdated={lastUpdated} wsConnected={wsConnected} />
      <MarketStatusStrip markets={status} />
      <KeyIndicesGrid indices={indices} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MoversList title="Top Gainers" rows={gainers} accent="text-[#10B981]" testidBase="live-gainer" Icon={TrendUp} />
        <MoversList title="Top Losers" rows={losers} accent="text-[#EF4444]" testidBase="live-loser" Icon={TrendDown} />
      </div>
      <FiiDiiPanel flows={fiiDii} />
      <CorpActionsPanel actions={corpActions} />
    </div>
  );
}

function TopBar({ onRefresh, loading, lastUpdated, wsConnected }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Broadcast size={20} weight="duotone" className={wsConnected ? 'text-[#00FF66] animate-pulse' : 'text-[#94A3B8]'} />
        <div>
          <p className="text-sm text-white font-medium flex items-center gap-2">
            Live from NSE India + BSE India
            <span className={`text-[10px] font-mono px-1.5 py-0.5 ${wsConnected ? 'bg-[#00FF66]/10 text-[#00FF66]' : 'bg-white/5 text-[#64748B]'}`}>
              WS {wsConnected ? 'CONNECTED' : 'reconnecting…'}
            </span>
          </p>
          <p className="text-[10px] text-[#64748B]">
            {lastUpdated ? `Last tick ${lastUpdated.toLocaleTimeString('en-IN')} • WS every 15s • REST every ${REFRESH_SEC}s` : 'Fetching…'}
          </p>
        </div>
      </div>
      <button
        onClick={onRefresh}
        disabled={loading}
        className="border border-white/20 text-white hover:bg-white/5 px-3 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-50"
        data-testid="live-refresh-btn"
      >
        <ArrowsClockwise size={14} className={loading ? 'animate-spin' : ''} />
        Refresh
      </button>
    </div>
  );
}

function MarketStatusStrip({ markets }) {
  if (!markets.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="live-market-status">
      {markets.map((m) => {
        const isOpen = (m.status || '').toLowerCase().includes('open');
        const color = isOpen ? 'text-[#00FF66] border-[#00FF66]/40 bg-[#00FF66]/5' : 'text-[#94A3B8] border-white/10 bg-white/[0.02]';
        return (
          <div key={m.segment} className={`px-3 py-1.5 border text-xs ${color}`}>
            <span className="font-semibold">{m.segment}</span>
            <span className="text-[10px] uppercase tracking-wider ml-2">{m.status}</span>
            {m.last ? <span className="ml-2 font-mono text-white">₹{m.last}</span> : null}
          </div>
        );
      })}
    </div>
  );
}

const KEY_INDEX_SYMBOLS = ['NIFTY 50', 'NIFTY NEXT 50', 'NIFTY BANK', 'NIFTY IT', 'NIFTY AUTO', 'NIFTY PHARMA', 'NIFTY FMCG', 'NIFTY METAL'];

function KeyIndicesGrid({ indices }) {
  const map = new Map(indices.map((i) => [i.symbol, i]));
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3">Key NSE Indices</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="live-key-indices">
        {KEY_INDEX_SYMBOLS.map((sym) => {
          const idx = map.get(sym);
          if (!idx) return null;
          const positive = (idx.percent_change ?? 0) >= 0;
          const color = positive ? 'text-[#10B981]' : 'text-[#EF4444]';
          return (
            <div key={sym} className="p-3 border border-white/10 bg-white/[0.02]" data-testid={`live-index-${sym.replace(/\s+/g, '-')}`}>
              <div className="text-[10px] text-[#64748B] uppercase tracking-wide">{sym}</div>
              <div className="text-lg font-mono font-semibold text-white mt-0.5">₹{idx.last?.toLocaleString('en-IN')}</div>
              <div className={`text-xs font-mono ${color}`}>
                {positive ? '▲' : '▼'} {idx.variation?.toFixed(2)} ({idx.percent_change?.toFixed(2)}%)
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MoversList({ title, rows, accent, testidBase, Icon }) {
  return (
    <div>
      <p className={`text-[10px] uppercase tracking-[0.2em] mb-3 flex items-center gap-1.5 ${accent}`}>
        <Icon size={12} weight="fill" /> {title}
      </p>
      <div className="border border-white/10 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-white/5 text-[#94A3B8]">
              <th className="text-left py-2 px-3">Symbol</th>
              <th className="text-right py-2 px-3">LTP</th>
              <th className="text-right py-2 px-3">Change %</th>
              <th className="text-right py-2 px-3">Volume</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 10).map((r) => (
              <tr key={r.symbol} className="border-t border-white/5" data-testid={`${testidBase}-${r.symbol}`}>
                <td className="py-2 px-3 font-mono text-white">{r.symbol}</td>
                <td className="py-2 px-3 text-right font-mono text-white">{r.ltp ? formatINR(r.ltp) : '—'}</td>
                <td className={`py-2 px-3 text-right font-mono ${accent}`}>
                  {r.percent_change != null ? `${r.percent_change > 0 ? '+' : ''}${r.percent_change.toFixed(2)}%` : '—'}
                </td>
                <td className="py-2 px-3 text-right text-[#64748B] font-mono">
                  {r.traded_quantity ? Number(r.traded_quantity).toLocaleString('en-IN') : '—'}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={4} className="py-4 text-center text-[#64748B]">No data</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FiiDiiPanel({ flows }) {
  if (!flows.length) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3">FII / DII Activity (₹ Cr)</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {flows.map((f) => {
          const netPositive = (f.net_value_cr ?? 0) >= 0;
          const netColor = netPositive ? 'text-[#10B981]' : 'text-[#EF4444]';
          return (
            <div key={f.category} className="border border-white/10 p-4 bg-white/[0.02]" data-testid={`live-fiidii-${f.category}`}>
              <div className="flex items-baseline justify-between mb-2">
                <h4 className="text-sm font-bold text-white">{f.category}</h4>
                <span className="text-[10px] font-mono text-[#64748B]">{f.date}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <FlowStat label="Buy" value={f.buy_value_cr} color="text-[#10B981]" />
                <FlowStat label="Sell" value={f.sell_value_cr} color="text-[#EF4444]" />
                <FlowStat label="Net" value={f.net_value_cr} color={netColor} bold />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FlowStat({ label, value, color, bold }) {
  return (
    <div>
      <div className="text-[9px] text-[#64748B] uppercase">{label}</div>
      <div className={`${color} font-mono ${bold ? 'font-bold text-sm' : 'text-xs'}`}>
        {value != null ? `${value >= 0 ? '+' : ''}₹${value.toLocaleString('en-IN')}` : '—'}
      </div>
    </div>
  );
}

function CorpActionsPanel({ actions }) {
  if (!actions.length) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3">BSE Corporate Actions (upcoming)</p>
      <div className="border border-white/10 overflow-hidden">
        <table className="w-full text-xs" data-testid="live-corp-actions">
          <thead>
            <tr className="bg-white/5 text-[#94A3B8]">
              <th className="text-left py-2 px-3">Scrip</th>
              <th className="text-left py-2 px-3">Symbol</th>
              <th className="text-left py-2 px-3">Purpose</th>
              <th className="text-right py-2 px-3">Ex Date</th>
              <th className="text-right py-2 px-3">Record Date</th>
            </tr>
          </thead>
          <tbody>
            {actions.slice(0, 15).map((a) => (
              <tr key={`${a.scrip_code}-${a.ex_date}-${a.purpose}`} className="border-t border-white/5">
                <td className="py-2 px-3 text-[#94A3B8] font-mono">{a.scrip_code}</td>
                <td className="py-2 px-3 text-white font-mono">{a.symbol}</td>
                <td className="py-2 px-3 text-[#94A3B8]">{a.purpose}</td>
                <td className="py-2 px-3 text-right text-white font-mono">{a.ex_date || '—'}</td>
                <td className="py-2 px-3 text-right text-[#94A3B8] font-mono">{a.record_date || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
